import { items, rarityLevels, tiers } from '../data/index.js';
import { GameError } from '../errors.js';
import type { GameEvent, SummonResult } from '../events.js';
import type { Rng } from '../rng.js';
import type { BotUser, SummonedCategory } from '../state.js';
import type { ItemType, RarityLevel } from '../types.js';

/** `rpg/summon.ts#getUserRarityLevel`: highest level whose `required` is reached. */
export function rarityLevelFor(category: SummonedCategory): RarityLevel {
  let level = rarityLevels[0] as RarityLevel;
  for (const r of rarityLevels) {
    if (category.total >= r.required) level = r;
    else break;
  }
  return level;
}

export interface SummonLevelInfo {
  /** Number of rarity levels reached (0..rarityLevels.length). */
  level: number;
  /** Summons made since the current level started. */
  xp: number;
  /** Summons the current level spans (1 at max). */
  next: number;
  max: boolean;
}

/** `data/items.ts#getSummonLevel` without the emoji progress bar. */
export function summonLevel(category: SummonedCategory): SummonLevelInfo {
  let level = 0;
  for (const r of rarityLevels) {
    if (category.total < r.required) break;
    level++;
  }
  const prevRequired = rarityLevels[level - 1]?.required ?? 0;
  const current = rarityLevels[level];
  const max = level >= rarityLevels.length || !current;
  return { level, xp: category.total - prevRequired, next: max ? 1 : current.required - prevRequired, max };
}

/** Tier ids from highest to lowest — the order the odds table is written in. */
const RARITY_ORDER = [...tiers].reverse().map((t) => t.id);

/**
 * `rpg/summon.ts#summon`: 1 lootcoin per item. Rolls rarity against the
 * cumulative slices, then a uniform item of that tier. Parity K-4: the
 * rarity level is re-read only when `total > required`.
 */
export function summon(save: BotUser, type: ItemType, amount: number, rng: Rng, events: GameEvent[]): SummonResult[] {
  if (!Number.isInteger(amount) || amount < 1) throw new GameError('invalidAmount');
  if (save.lootbox < amount) throw new GameError('notEnoughLootcoins', { needed: amount, have: save.lootbox });

  const category = save.summoned[type];
  const pool = items[type];
  let odds = rarityLevelFor(category);
  const results: SummonResult[] = [];

  for (let i = 0; i < amount; i++) {
    if (category.total > odds.required) odds = rarityLevelFor(category);

    const r = rng.next();
    let rarity = 1;
    let acc = 0;
    for (let j = 0; j < RARITY_ORDER.length; j++) {
      acc += odds.tiers[j] ?? 0;
      if (r <= acc) {
        rarity = RARITY_ORDER[j] ?? 1;
        break;
      }
    }
    category.total += 1;

    const ofRarity = pool.filter((it) => it.tier === rarity);
    const item = ofRarity[Math.floor(rng.next() * ofRarity.length)];
    if (!item) continue;

    let owned = category.owned.find((o) => o.id === item.id);
    const isNew = !owned || owned.total === 0;
    if (owned) {
      owned.total += 1;
      owned.current += 1;
    } else {
      owned = { id: item.id, level: 1, total: 1, current: 1 };
      category.owned.push(owned);
    }
    results.push({ id: item.id, tier: item.tier, isNew });
  }

  save.lootbox -= amount;
  save.stats.summoned.total += amount;
  events.push({ type: 'itemsSummoned', itemType: type, amount, results });
  return results;
}
